import React, {useState, useMemo} from 'react';
import "./index.css";
import FileList, {FileListItem} from "../FileList";
import {useMutation, useQuery} from "@apollo/client";
import {AssetFiles} from "../../graphql/queries/assetFiles";
import _ from "lodash";
import {Box, Chip, ToggleButton, ToggleButtonGroup} from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import {useTranslation} from "react-i18next";
import {DescriptionOutlined, UploadRounded, FileUploadOffRounded, CloseRounded} from "@mui-symbols-material/w200";
import {FilterListRounded, GridViewRounded, ViewListRounded} from "@mui-symbols-material/w300";
import {FileListViewMode, loadFileListViewMode, saveFileListViewMode} from "../../utils/fileListViewMode";
import {DropzoneArea, uploadFilesRequest, WidgetBox} from "@dreebit-registry/vsm-microservice-components";
import {resolveErrorMessage, sanitizeErrorForDisplay} from "../../utils/resolveErrorMessage";
import {useKeycloak} from "@react-keycloak/web";
import {toast} from "react-toastify";
import {AddFilesToInstalledBaseNode} from "../../graphql/mutations/addFilesToInstalledBaseNode";
import {useTheme} from "@mui/material/styles";
import {Typography} from '@mui/joy';
import CircularProgress from '@mui/joy/CircularProgress';
import {Error} from "@dreebit-registry/vsm-microservice-components";
import FileListFilter from "../FilesListFilter";
import {ProfileLanguage} from "../../graphql/queries/profileLanguage";
import {buildNodeImageTarget, nodeTypeLabelForSentence} from "../../hooks/useNodeImage";
import {getInstalledBaseNodeTypeName} from "../../utils/getInstalledBaseNodeTypeName";

interface Props {
    assetLocationId: string;
    installedBaseNodeId: string;
    isLoadingInstalledBaseNode: boolean;
}

const AssetFileManagement = ({assetLocationId, installedBaseNodeId, isLoadingInstalledBaseNode}: Props) => {
    const {t, i18n } = useTranslation();
    const theme = useTheme();
    const { keycloak } = useKeycloak();

    const profileLanguageQuery = useQuery(ProfileLanguage)
    const userLanguage = _.get(profileLanguageQuery, 'data.profile.language')

    const assetFilesQuery = useQuery(AssetFiles, {
        variables: {
            id: assetLocationId,
        }
    })

    const [addFilesToInstalledBaseNodeMutation] = useMutation(AddFilesToInstalledBaseNode)

    const [dropzoneKey, setDropzoneKey] = useState(0);

    const [viewMode, setViewMode] = useState<FileListViewMode>(() => loadFileListViewMode());

    const handleViewChange = (_event: React.MouseEvent<HTMLElement>, newMode: FileListViewMode | null) => {
        if (!newMode) return; // ignore deselect → keep one option active
        setViewMode(newMode);
        saveFileListViewMode(newMode);
    };

    const installedBaseNodeFiles = _.get(assetFilesQuery, 'data.assetLocation.installedBaseNode.fileList.installedBaseNodeFiles', []) ?? []
    const productNodeFiles = _.get(assetFilesQuery, 'data.assetLocation.asset.product.fileList.productNodeFiles', []) ?? []
    const isLoading = assetFilesQuery?.loading;
    // "Use as asset image" on image files — needs key/title for the update mutation.
    const fileNode = _.get(assetFilesQuery, 'data.assetLocation.installedBaseNode');
    const nodeImageTarget = buildNodeImageTarget(
        fileNode,
        nodeTypeLabelForSentence(fileNode ? getInstalledBaseNodeTypeName(fileNode.nodeType, t) : undefined, i18n.language)
    ) ?? undefined;

    const allFiles: FileListItem[] = useMemo(() => {
        const userFiles: FileListItem[] = installedBaseNodeFiles
            .filter(Boolean)
            .map((f: any) => ({
                id: f.id,
                fileId: f.file?.id ?? null,
                filename: f.filename,
                basename: f.file?.basename ?? '',
                extension: f.file?.extension ?? null,
                comment: f.comment ?? null,
                supportsComment: true,
                category: f.category ?? t("User files"),
                insert: f.insert ?? null,
                contentType: f.file?.contentType ?? '',
                size: f.file?.size ?? 0,
                link: f.file?.link ?? '',
                thumbnailLink: f.file?.thumbnailLink ?? null,
                previewLink: f.file?.previewLink ?? null,
                language: f.file?.language ?? null,
                access: f.access ?? { read: true, write: false, delete: false },
                kind: 'installedBaseNode',
            }));

        const productFiles: FileListItem[] = productNodeFiles
            .filter(Boolean)
            .map((f: any) => ({
                id: f.id,
                fileId: f.file?.id ?? null,
                filename: f.filename,
                basename: f.file?.basename ?? '',
                extension: f.file?.extension ?? null,
                comment: null,
                supportsComment: false,
                category: f.category ?? null,
                insert: f.insert ?? null,
                contentType: f.file?.contentType ?? '',
                size: f.file?.size ?? 0,
                link: f.file?.link ?? '',
                thumbnailLink: f.file?.thumbnailLink ?? null,
                previewLink: f.file?.previewLink ?? null,
                language: f.file?.language ?? null,
                access: { read: true, write: false, delete: false },
                kind: 'assetLocation',
            }));

        return [...userFiles, ...productFiles];
    }, [installedBaseNodeFiles, productNodeFiles, t]);

    const uploadFiles = async (files: File[]) => {
        if (files && files.length > 0) {
            try {
                // upload the files to the file microservice (POST /file)
                const uploadedFiles = await uploadFilesRequest(files, keycloak.token);

                const mutationInput = uploadedFiles.map((file) => {
                    return {
                        filename: file.filename,
                        fileServiceUuid: file.fileId,
                    }
                })

                // add the uploaded files to the product
                const res = await addFilesToInstalledBaseNodeMutation({
                    variables: {
                        installedBaseNodeId: installedBaseNodeId,
                        input: mutationInput
                    },
                    refetchQueries: ["AssetFiles"]
                })

                if (res?.data?.addFilesToInstalledBaseNode?.length > 0) {
                    toast.success(t("File(s) uploaded succesfully."));
                }
            } catch (err) {
                toast.error(resolveErrorMessage(err, t))
            } finally {
                setDropzoneKey((k) => k + 1);
            }
        }
    }

    const [activeFilters, setActiveFilters] = useState<Record<string, any>>({});


    const handleFilterChange = (values: Record<string, any>) => {
        // values sieht dann z.B. so aus: { language: 'de', category: null }
        setActiveFilters(values);
    }

    const filteredFiles = useMemo(() => {
        if (!allFiles|| allFiles.length === 0) return [];

        return allFiles.filter((file) => {

            if (activeFilters.language && activeFilters.language.length > 0) {
                if (file.language && !activeFilters.language.includes(file.language)) {
                    return false;
                }
            }

            if (activeFilters.category && activeFilters.category.length > 0) {
                if (!activeFilters.category.includes(file.category)) {
                    return false;
                }
            }

            return true;
        });
    }, [allFiles, activeFilters]);

    return (
        <Box sx={{display: 'flex', width: '100%', maxWidth: '100%', boxSizing: 'border-box', overflowX: 'hidden', flex: 1}}>

            <WidgetBox
                title={t("Documents")}
                titleIcon={<DescriptionOutlined sx={{color: '#333', fontSize: 28}} />}
                sx={{display: 'flex', flexDirection: 'column', flex: { md: 2, lg: 4 }, minWidth: 0, border: 'none', px: '13px', pt: '0', pb: '0', gap: '21px'}}
            >
                {/* Filter-Chips (links) und Ansichtsumschalter (rechts) in einer Zeile */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', minHeight: '32px' }}>

                    {/* Bereich für die zusammengefassten Filter-Chips */}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px', flex: 1, minWidth: 0 }}>

                    {/* 1. Zusammengefasster Sprach-Chip (MUI) */}
                    {activeFilters.language && activeFilters.language.length > 0 && (
                        <Chip
                            key="summary-language"
                            size="medium"
                            variant="filled"
                            label={
                                <Box
                                    sx={{
                                        maxWidth: 160,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                    }}
                                    // Nativer Tooltip mit allen ausgewählten Sprachen
                                    title={activeFilters.language
                                        .map((lang: string) => new Intl.DisplayNames([i18n.language], { type: 'language' }).of(lang) || lang)
                                        .join(', ')
                                    }
                                >
                                    {t("Language")}: {activeFilters.language.length === 1
                                    ? new Intl.DisplayNames([i18n.language], { type: 'language' }).of(activeFilters.language[0])
                                    : activeFilters.language.length}
                                </Box>
                            }
                            onDelete={() => setActiveFilters(prev => ({ ...prev, language: [] }))}
                            deleteIcon={<CloseRounded sx={{color: "#333 !important"}}/>}
                        />
                    )}

                    {/* 2. Zusammengefasster Kategorie-Chip (MUI) */}
                    {activeFilters.category && activeFilters.category.length > 0 && (
                        <Chip
                            key="summary-category"
                            size="medium"
                            variant="filled"
                            label={
                                <Box
                                    sx={{
                                        maxWidth: 160,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                    }}
                                    // Nativer Tooltip mit allen ausgewählten Kategorien
                                    title={activeFilters.category.map((cat: string) => t(cat)).join(', ')}
                                >
                                    {t("Category")}: {activeFilters.category.length === 1
                                    ? t(activeFilters.category[0])
                                    : activeFilters.category.length}
                                </Box>
                            }
                            onDelete={() => setActiveFilters(prev => ({ ...prev, category: [] }))}
                            deleteIcon={<CloseRounded sx={{color: "#333 !important"}}/>}
                        />
                    )}
                    </Box>

                    {/* Ansicht umschalten (Liste / Kacheln) */}
                    <ToggleButtonGroup
                        value={viewMode}
                        exclusive
                        size="small"
                        onChange={handleViewChange}
                        aria-label={t("View")}
                        sx={{flexShrink: 0, height: '32px'}}
                    >
                        <ToggleButton value="list" aria-label={t("List view")} sx={{px: '8px', py: 0}}>
                            <Tooltip title={t("List view")}>
                                <ViewListRounded sx={{fontSize: 20}} />
                            </Tooltip>
                        </ToggleButton>
                        <ToggleButton value="grid" aria-label={t("Tile view")} sx={{px: '8px', py: 0}}>
                            <Tooltip title={t("Tile view")}>
                                <GridViewRounded sx={{fontSize: 20}} />
                            </Tooltip>
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Box>

                {isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '300px' }}>
                        <CircularProgress variant="soft" />
                    </Box>
                ) : assetFilesQuery.error ? (

                    <Error errorObject={sanitizeErrorForDisplay(assetFilesQuery.error, t)}/>

                ) : (
                    <Box>
                        <FileList files={filteredFiles} userLanguage={userLanguage} viewMode={viewMode} nodeImageTarget={nodeImageTarget} />
                    </Box>
                )}
            </WidgetBox>
            <Box sx={{
                display:'flex', flexDirection:'column', flex: 1, justifyContent: 'space-between', maxWidth:'350px', minWidth: '250px',
                gap: '13px', pl: '21px', pr:'8px', pt:'13px', borderLeft: "1px solid #cccccc", ml: '12px'
            }}>

                <Box>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: '8px', pb: '21px'}}>

                        <FilterListRounded/>
                        <Typography level = "titleMedium">
                            {t("Filter")}
                        </Typography>
                    </Box>

                    <Box sx={{display: 'flex', alignItems: 'center', gap: '8px', width: '100%',}}>
                        {isLoading ? (
                            <Box></Box>

                        ) : (
                            <FileListFilter
                                files={allFiles}
                                onValuesChange={handleFilterChange}
                                userLanguage={userLanguage}
                                onReset={() => {
                                    setActiveFilters({});
                                }}
                                initialValues={activeFilters}
                            />
                        )}
                    </Box>

                </Box>

                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', aspectRatio: '1/1'}}>
                    {
                        isLoadingInstalledBaseNode ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', aspectRatio: '1/1', width: '100%' }}>

                            </Box>
                        ) : installedBaseNodeId ? (
                            <DropzoneArea
                                key={dropzoneKey}
                                theme={theme}
                                fileObjects={[]}
                                onChange={(loadedFiles: File[]) => {
                                    uploadFiles(loadedFiles)
                                }}
                                onDelete={() => {}}
                                filesLimit={10}
                                maxFileSize={999999999}
                                showPreviewsInDropzone={false}
                                showFileNames={true}
                                dropzoneText={t("Click here or drag files to this area to upload.")}
                                dropzoneClass={"asset-file-management-file-upload-dropzone"}
                                dropzoneParagraphClass={"custom-file-upload-textbox"}
                                showAlerts={['error']}
                                Icon={UploadRounded}
                                classes={{
                                    text: "asset-file-management-dropzone-text",
                                    root: "dropzone-root",
                                    icon: "asset-file-management-dropzone-icon"
                                }}
                            />
                        ) : (
                            <Box sx={{
                                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                                textAlign: 'center', aspectRatio: '1/1', border: '1px dashed #cccccc', borderRadius:'4px', gap: '21px', p: '21px'}}>
                                <Typography level="titleMediumLight">
                                    {t("Please install this asset to add files.")}
                                </Typography>
                                <FileUploadOffRounded sx={{ width: 55, height: 55}}/>
                            </Box>
                        )
                    }
                </Box>

            </Box>


        </Box>

    );
}

export default AssetFileManagement;
